import { Outlet } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@/lib/utils/utils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { toggleMobileSidebar } from '@/store/slices/uiSlice';

import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Toaster } from '@/components/ui/toast';
import ScrollToTop from '../navigation/ScrollToTop';

interface LayoutProps {
  children?: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const dispatch = useAppDispatch();
  const { isMobileSidebarOpen, sidebarCollapsed } = useAppSelector((state) => state.ui);

  return (
    <div className="flex h-screen overflow-hidden bg-gradient-to-br from-blue-50/30 via-white to-indigo-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900/20">
      {/* Global toast system */}
      <Toaster />

      {/* Desktop Sidebar */}
      <div className="relative z-30 hidden lg:flex lg:flex-shrink-0">
        <motion.div
          initial={{ x: -280 }}
          animate={{ x: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <Sidebar />
        </motion.div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
              onClick={() => dispatch(toggleMobileSidebar())}
            />

            <motion.div
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed inset-y-0 left-0 z-50 w-72 lg:hidden"
            >
              <Sidebar onNavigate={() => dispatch(toggleMobileSidebar())} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div
        className={cn(
          'relative flex min-w-0 flex-1 flex-col overflow-hidden',
          'lg:transition-[margin] lg:duration-300',
          // Shift content slightly when collapsing to keep visual balance
          sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-0',
        )}
      >
        <Header />
        <ScrollToTop />

        <main
          id="app-scroll-container"
          role="main"
          className={cn(
            'flex-1 overflow-y-auto transition-all duration-300',
            'bg-[#F7F9FC] dark:bg-gray-700/80',
            'border-l border-gray-200/50 dark:border-gray-700/50',
          )}
        >
          <div className="max-w-8xl mx-auto space-y-6">
            <div className="m-4">{children || <Outlet />}</div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
